import React, { useEffect, useState, useCallback } from "react";
import { Power, Crown, Loader2, Gift } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export const BeaconLaunchWindow = () => {
  const [win, setWin] = useState(null);
  const [subs, setSubs] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [w, s] = await Promise.all([
        api.get("/admin/beacon/launch-window"),
        api.get("/admin/beacon/subscriptions"),
      ]);
      setWin(w.data); setSubs(s.data.subscriptions);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    if (!win) return;
    const next = !win.open;
    if (!next && !window.confirm("Close the Free Launch Window? Standard pricing takes effect and existing free Beacons get 30 days to choose a tier.")) return;
    setBusy(true);
    try { await api.put("/admin/beacon/launch-window", { open: next }); toast.success(next ? "Free window re-opened" : "Free window closed, pricing is now live"); load(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const comp = async (beacon_id) => {
    try { await api.post("/admin/beacon/comp", { beacon_id, tier: "tier1" }); toast.success("Hosting comped"); load(); }
    catch { toast.error("Failed"); }
  };

  if (!win) return null;

  return (
    <div className="border border-[#14A89A]/30 bg-[#121212] p-5 mb-6" data-testid="beacon-launch-window">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <h3 className="font-body text-white font-medium flex items-center gap-2"><Power size={16} className="text-[#14A89A]" /> Free Launch Window</h3>
          <p className="font-body text-xs text-zinc-500 mt-1">While on, activating a Beacon is free and tags the account <span className="text-amber-300">Founding</span> (permanent 40% off once pricing begins). {win.founding_count} Founding Beacon{win.founding_count === 1 ? "" : "s"} so far.</p>
        </div>
        <button onClick={toggle} disabled={busy} data-testid="toggle-launch-window"
          className={`px-4 py-2 font-body text-xs font-semibold border transition-colors ${win.open ? "bg-[#14A89A] text-[#0A0A0A] border-[#14A89A]" : "border-white/20 text-zinc-300 hover:bg-white/5"}`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : win.open ? "ON: hosting is free" : "OFF: pricing live"}
        </button>
      </div>
      {win.stripe && (
        <p data-testid="stripe-status" className="font-body text-[11px] text-zinc-500">
          Stripe: <span data-testid="stripe-mode" className={win.stripe.mode === "live" ? "text-[#14A89A]" : "text-amber-300"}>{win.stripe.mode === "live" ? "LIVE" : "TEST mode (no real charges)"}</span>
          {" · "}Prices: <span data-testid="stripe-catalog" className={win.stripe.catalog_ok ? "text-[#14A89A]" : "text-amber-300"}>{win.stripe.catalog_ok === null ? "checking…" : win.stripe.catalog_ok ? "synced ($19 / $53 / $107 founding)" : `not synced: ${win.stripe.catalog_error}`}</span>
        </p>
      )}
      {!win.open && win.closed_at && <p className="font-body text-[11px] text-amber-400/80">Closed {new Date(win.closed_at).toLocaleDateString()} · free Beacons have 30 days to pick a tier.</p>}

      {subs && subs.length > 0 && (
        <div className="mt-4 border-t border-white/10 pt-4">
          <h4 className="font-body text-xs uppercase tracking-wide text-zinc-500 mb-2">Hosting subscriptions</h4>
          <div className="space-y-2">
            {subs.map((s) => (
              <div key={s.beacon_id} data-testid={`sub-row-${s.beacon_id}`} className="flex items-center justify-between gap-2 text-sm flex-wrap">
                <div className="min-w-0">
                  <span className="font-body text-zinc-200">{s.title || s.owner_name}</span>
                  {s.founding && <Crown size={11} className="inline ml-1 text-amber-300" />}
                  <span className="ml-2 font-body text-[11px] text-zinc-500">{s.hosting_status}{s.tier ? ` · ${s.tier}` : ""} · {s.storage_used_gb} GB</span>
                </div>
                <button onClick={() => comp(s.beacon_id)} data-testid={`comp-${s.beacon_id}`} className="flex items-center gap-1 text-[11px] font-body text-[#14A89A] hover:text-white"><Gift size={12} /> Comp</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BeaconLaunchWindow;
